export const DATABASE_NAME = 'amalthea-toir-offline'
export const DATABASE_VERSION = 1

export interface CachedRecord<T = unknown> {
  id: string
  userId: string
  key: string
  value: T
  fetchedAt: number
  expiresAt?: number
}

/**
 * obsolete — сервер отклонил действие насовсем (работа уже завершена или отменена, запись удалена, данные не проходят
 * проверку): оно не блокирует остальные действия и не ждёт решения, а лежит в списке «Отклонено сервером».
 */
export type OutboxState = 'queued' | 'syncing' | 'retry' | 'auth-required' | 'conflict' | 'failed' | 'obsolete'
export type OutboxBodyType = 'json' | 'multipart'

export interface OutboxRecord {
  id: string
  userId: string
  label: string
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  path: string
  headers?: Record<string, string>
  bodyType: OutboxBodyType
  jsonBody?: unknown
  fields?: Record<string, string>
  fileBlob?: Blob
  fileName?: string
  fileType?: string
  fileSize?: number
  idempotencyKey: string
  state: OutboxState
  attempts: number
  createdAt: number
  updatedAt: number
  nextAttemptAt: number
  lastError?: string
  responseStatus?: number
  /** Код ошибки API (например, WORK_ORDER_LOCKED) — для диагностики и отчёта устройства. */
  errorCode?: string
  leaseOwner?: string
  leaseExpiresAt?: number
}

export const OUTBOX_LEASE_MS = 2 * 60_000

export interface MetaRecord<T = unknown> {
  key: string
  value: T
}

let databasePromise: Promise<IDBDatabase> | null = null

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Ошибка локального хранилища'))
  })
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('Ошибка локального хранилища'))
    transaction.onabort = () => reject(transaction.error ?? new Error('Операция локального хранилища отменена'))
  })
}

function storageWriteError(error: unknown): Error {
  if (error instanceof DOMException && ['QuotaExceededError', 'NS_ERROR_DOM_QUOTA_REACHED'].includes(error.name)) {
    return new Error('На устройстве недостаточно свободного места. Освободите место и повторите действие')
  }
  return error instanceof Error ? error : new Error('Не удалось сохранить данные на устройстве')
}

export function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise

  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains('cache')) {
        const cache = database.createObjectStore('cache', { keyPath: 'id' })
        cache.createIndex('userId', 'userId')
        cache.createIndex('expiresAt', 'expiresAt')
      }
      if (!database.objectStoreNames.contains('outbox')) {
        const outbox = database.createObjectStore('outbox', { keyPath: 'id' })
        outbox.createIndex('userId', 'userId')
        outbox.createIndex('state', 'state')
        outbox.createIndex('nextAttemptAt', 'nextAttemptAt')
      }
      if (!database.objectStoreNames.contains('meta')) {
        database.createObjectStore('meta', { keyPath: 'key' })
      }
    }
    request.onsuccess = () => {
      const database = request.result
      database.onversionchange = () => database.close()
      resolve(database)
    }
    request.onerror = () => {
      databasePromise = null
      reject(request.error ?? new Error('Не удалось открыть локальное хранилище'))
    }
    request.onblocked = () => {
      databasePromise = null
      reject(new Error('Закройте другие вкладки приложения и повторите'))
    }
  })
  return databasePromise
}

function cacheId(userId: string, key: string) {
  return `${userId}::${key}`
}

export async function putCached<T>(userId: string, key: string, value: T, maxAgeMs?: number): Promise<void> {
  try {
    const database = await openDatabase()
    const transaction = database.transaction('cache', 'readwrite')
    const now = Date.now()
    transaction.objectStore('cache').put({
      id: cacheId(userId, key),
      userId,
      key,
      value,
      fetchedAt: now,
      expiresAt: maxAgeMs ? now + maxAgeMs : undefined,
    } satisfies CachedRecord<T>)
    await transactionComplete(transaction)
  } catch (error) {
    throw storageWriteError(error)
  }
}

export async function getCached<T>(userId: string, key: string): Promise<CachedRecord<T> | null> {
  const database = await openDatabase()
  const transaction = database.transaction('cache', 'readonly')
  const result = await requestValue(transaction.objectStore('cache').get(cacheId(userId, key)))
  await transactionComplete(transaction)
  return (result as CachedRecord<T> | undefined) ?? null
}

export async function deleteCached(userId: string, key: string): Promise<void> {
  const database = await openDatabase()
  const transaction = database.transaction('cache', 'readwrite')
  transaction.objectStore('cache').delete(cacheId(userId, key))
  await transactionComplete(transaction)
}

export async function invalidateCache(userId: string, keyPrefix = ''): Promise<void> {
  const database = await openDatabase()
  const transaction = database.transaction('cache', 'readwrite')
  const index = transaction.objectStore('cache').index('userId')
  const request = index.openCursor(IDBKeyRange.only(userId))
  request.onsuccess = () => {
    const cursor = request.result
    if (!cursor) return
    const record = cursor.value as CachedRecord
    // Preserve the last known good response for offline use. An expired marker
    // makes online queries refresh it without turning the next outage into a
    // cache miss.
    if (!keyPrefix || record.key.startsWith(keyPrefix)) cursor.update({ ...record, expiresAt: 0 })
    cursor.continue()
  }
  await transactionComplete(transaction)
}

export async function putOutbox(record: OutboxRecord): Promise<void> {
  try {
    const database = await openDatabase()
    const transaction = database.transaction('outbox', 'readwrite')
    transaction.objectStore('outbox').put(record)
    await transactionComplete(transaction)
  } catch (error) {
    throw storageWriteError(error)
  }
}

export async function getOutbox(id: string): Promise<OutboxRecord | null> {
  const database = await openDatabase()
  const transaction = database.transaction('outbox', 'readonly')
  const result = await requestValue(transaction.objectStore('outbox').get(id))
  await transactionComplete(transaction)
  return (result as OutboxRecord | undefined) ?? null
}

export async function listOutbox(userId: string): Promise<OutboxRecord[]> {
  const database = await openDatabase()
  const transaction = database.transaction('outbox', 'readonly')
  const result = await requestValue(transaction.objectStore('outbox').index('userId').getAll(userId))
  await transactionComplete(transaction)
  return (result as OutboxRecord[]).sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))
}

export type DeleteOutboxResult = 'deleted' | 'active' | 'missing'

/**
 * Deletes a user-discarded record only when no sync request owns it. The read
 * and delete share one IndexedDB transaction, so a concurrent tab cannot claim
 * the record between the guard and deletion.
 */
export async function deleteOutboxIfIdle(id: string): Promise<DeleteOutboxResult> {
  const database = await openDatabase()
  const transaction = database.transaction('outbox', 'readwrite')
  const store = transaction.objectStore('outbox')
  const existing = await requestValue(store.get(id)) as OutboxRecord | undefined
  if (!existing) {
    await transactionComplete(transaction)
    return 'missing'
  }
  if (existing.state === 'syncing') {
    await transactionComplete(transaction)
    return 'active'
  }
  store.delete(id)
  await transactionComplete(transaction)
  return 'deleted'
}

export async function updateOutbox(id: string, changes: Partial<OutboxRecord>): Promise<OutboxRecord | null> {
  const database = await openDatabase()
  const transaction = database.transaction('outbox', 'readwrite')
  const store = transaction.objectStore('outbox')
  const existing = await requestValue(store.get(id)) as OutboxRecord | undefined
  if (!existing) {
    await transactionComplete(transaction)
    return null
  }
  const updated = { ...existing, ...changes, id, updatedAt: Date.now() }
  store.put(updated)
  await transactionComplete(transaction)
  return updated
}

/**
 * Atomically acquires a short-lived record lease. IndexedDB serializes
 * read/write transactions for this object store across tabs, so only one
 * tab can turn the same queued record into its owned `syncing` record.
 */
export async function claimOutbox(
  id: string,
  userId: string,
  leaseOwner: string,
  now = Date.now(),
  leaseDurationMs = OUTBOX_LEASE_MS,
): Promise<OutboxRecord | null> {
  const database = await openDatabase()
  const transaction = database.transaction('outbox', 'readwrite')
  const store = transaction.objectStore('outbox')
  const existing = await requestValue(store.get(id)) as OutboxRecord | undefined

  const legacyLeaseExpiry = existing?.updatedAt === undefined ? 0 : existing.updatedAt + leaseDurationMs
  const leaseExpiry = existing?.leaseExpiresAt ?? legacyLeaseExpiry
  const claimable = Boolean(
    existing
      && existing.userId === userId
      && !['auth-required', 'conflict', 'failed', 'obsolete'].includes(existing.state)
      && existing.nextAttemptAt <= now
      && (existing.state !== 'syncing' || leaseExpiry <= now),
  )

  if (!claimable || !existing) {
    await transactionComplete(transaction)
    return null
  }

  const claimed: OutboxRecord = {
    ...existing,
    state: 'syncing',
    attempts: existing.attempts + 1,
    updatedAt: now,
    lastError: undefined,
    responseStatus: undefined,
    leaseOwner,
    leaseExpiresAt: now + leaseDurationMs,
  }
  store.put(claimed)
  await transactionComplete(transaction)
  return claimed
}

/** Updates a claimed record only while the caller still owns its lease. */
export async function updateClaimedOutbox(
  id: string,
  leaseOwner: string,
  changes: Partial<OutboxRecord>,
): Promise<OutboxRecord | null> {
  const database = await openDatabase()
  const transaction = database.transaction('outbox', 'readwrite')
  const store = transaction.objectStore('outbox')
  const existing = await requestValue(store.get(id)) as OutboxRecord | undefined
  if (!existing || existing.state !== 'syncing' || existing.leaseOwner !== leaseOwner) {
    await transactionComplete(transaction)
    return null
  }

  const updated: OutboxRecord = { ...existing, ...changes, id, updatedAt: Date.now() }
  if (updated.state !== 'syncing') {
    delete updated.leaseOwner
    delete updated.leaseExpiresAt
  }
  store.put(updated)
  await transactionComplete(transaction)
  return updated
}

/** Deletes a completed record only while the caller still owns its lease. */
export async function deleteClaimedOutbox(id: string, leaseOwner: string): Promise<boolean> {
  const database = await openDatabase()
  const transaction = database.transaction('outbox', 'readwrite')
  const store = transaction.objectStore('outbox')
  const existing = await requestValue(store.get(id)) as OutboxRecord | undefined
  if (!existing || existing.state !== 'syncing' || existing.leaseOwner !== leaseOwner) {
    await transactionComplete(transaction)
    return false
  }
  store.delete(id)
  await transactionComplete(transaction)
  return true
}

/** Удаляет записи пользователя по условию; запись, которая отправляется прямо сейчас (действующая аренда), остаётся. */
export async function deleteOutboxRecords(userId: string, predicate: (record: OutboxRecord) => boolean, now = Date.now()): Promise<number> {
  const database = await openDatabase()
  const transaction = database.transaction('outbox', 'readwrite')
  const index = transaction.objectStore('outbox').index('userId')
  const request = index.openCursor(IDBKeyRange.only(userId))
  let deleted = 0
  request.onsuccess = () => {
    const cursor = request.result
    if (!cursor) return
    const record = cursor.value as OutboxRecord
    const leased = record.state === 'syncing' && (record.leaseExpiresAt ?? (record.updatedAt + OUTBOX_LEASE_MS)) > now
    if (!leased && predicate(record)) {
      cursor.delete()
      deleted += 1
    }
    cursor.continue()
  }
  await transactionComplete(transaction)
  return deleted
}

export async function resumeAuthRequiredOutbox(userId: string): Promise<number> {
  const database = await openDatabase()
  const transaction = database.transaction('outbox', 'readwrite')
  const index = transaction.objectStore('outbox').index('userId')
  const request = index.openCursor(IDBKeyRange.only(userId))
  const now = Date.now()
  let resumed = 0
  request.onsuccess = () => {
    const cursor = request.result
    if (!cursor) return
    const record = cursor.value as OutboxRecord
    if (record.state === 'auth-required') {
      const updated: OutboxRecord = {
        ...record,
        state: 'queued',
        updatedAt: now,
        nextAttemptAt: now,
        lastError: undefined,
        responseStatus: undefined,
      }
      delete updated.leaseOwner
      delete updated.leaseExpiresAt
      cursor.update(updated)
      resumed += 1
    }
    cursor.continue()
  }
  await transactionComplete(transaction)
  return resumed
}

export async function putMeta<T>(key: string, value: T): Promise<void> {
  const database = await openDatabase()
  const transaction = database.transaction('meta', 'readwrite')
  transaction.objectStore('meta').put({ key, value } satisfies MetaRecord<T>)
  await transactionComplete(transaction)
}

export async function getMeta<T>(key: string): Promise<T | null> {
  const database = await openDatabase()
  const transaction = database.transaction('meta', 'readonly')
  const result = (await requestValue(transaction.objectStore('meta').get(key))) as MetaRecord<T> | undefined
  await transactionComplete(transaction)
  return result?.value ?? null
}

export async function deleteMeta(key: string): Promise<void> {
  const database = await openDatabase()
  const transaction = database.transaction('meta', 'readwrite')
  transaction.objectStore('meta').delete(key)
  await transactionComplete(transaction)
}

export async function clearUserPartition(userId: string): Promise<void> {
  const database = await openDatabase()
  const transaction = database.transaction(['cache', 'outbox'], 'readwrite')

  for (const storeName of ['cache', 'outbox'] as const) {
    const request = transaction.objectStore(storeName).index('userId').openCursor(IDBKeyRange.only(userId))
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) return
      cursor.delete()
      cursor.continue()
    }
  }
  await transactionComplete(transaction)
}

export async function resetDatabaseForTests(): Promise<void> {
  if (databasePromise) {
    const database = await databasePromise.catch(() => null)
    database?.close()
    databasePromise = null
  }
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
    request.onblocked = () => resolve()
  })
}
